import React, { useState } from 'react';
import { Link } from 'react-router';
import {
    Accessibility,
    ArrowLeft,
    ArrowRight,
    BadgeCheck,
    Building2,
    Check,
    ChevronRight,
    Gift,
    Headphones,
    HeartHandshake,
    Mail,
    MailPlus,
    MessageCircle,
    RefreshCcw,
    Sparkles,
    Star,
    Users
} from 'lucide-react';
import ScreenLayout from './ScreenLayout';
import { useAuth } from '../context/AuthContext';
import { emitCrmEvent } from '../services/crmEvents';
import { getPremiumPreference, setPremiumPreference } from '../services/premiumPreference';

const offerings = [
    {
        id: 'premium_plan',
        icon: Star,
        eyebrow: 'Go deeper',
        title: 'Premium plan',
        description: 'A more personal prayer rhythm, with practical support that grows with you.',
        cta: 'Join the early list',
        features: [
            {
                label: 'Personalized prayer plan generated weekly',
                title: 'Example: your next seven days',
                example: 'A seven-day plan shaped around your current season, schedule, and prayer goals, with a focus, verse, and guided prompt for each day.',
                fulfillment: 'You choose a short welcome conversation or a thoughtful written intake. A real person reads it and personally reviews your plan before delivery.'
            },
            {
                label: 'Daily guided prayer prompts',
                title: 'Example: Tuesday’s guided prompt',
                example: '“Peace before a hard conversation” with a short pause, James 1:19, three reflection questions, and a closing prayer.',
                fulfillment: 'One focused prompt is delivered each day and tied back to the theme of your weekly plan.'
            },
            {
                label: 'Saved prayer history',
                title: 'Example: your private prayer timeline',
                example: 'A dated record of completed plans, saved prompts, personal notes, and prayers you want to revisit.',
                fulfillment: 'Your history stays private to your account and can be reviewed or removed by you.'
            },
            {
                label: 'Longer devotional guides',
                title: 'Example: an unhurried devotional',
                example: 'A seven-to-ten-minute guide with scripture context, a grounded reflection, questions to sit with, and a closing prayer.',
                fulfillment: 'New guides add depth beyond the short daily experience and are written for unrushed reflection.'
            },
            {
                label: 'Simple habit/reminder support',
                title: 'Example: a gentle prayer rhythm',
                example: 'Choose weekday mornings at 7:00 and receive one quiet reminder with a direct link to that day’s prayer.',
                fulfillment: 'You choose the days, time, and channel. Reminders can be paused or changed at any time.'
            },
            {
                label: 'Early access to premium features',
                title: 'Example: preview before release',
                example: 'Try a new prayer-plan format or delivery option before it is released publicly, then share what felt useful.',
                fulfillment: 'Early members receive an invitation, a plain explanation of the feature, and a direct way to give feedback.'
            }
        ]
    },
    {
        id: 'drip',
        icon: Mail,
        eyebrow: 'Daily encouragement',
        title: 'Verses delivered to you',
        description: 'A small, dependable moment of scripture delivered where you already begin your day.',
        cta: 'I’m interested',
        features: [
            {
                label: 'One Bible verse delivered daily',
                title: 'Example: today’s verse',
                example: 'At your chosen time, receive Psalm 46:10 with the translation clearly named and a link to read the surrounding passage.',
                fulfillment: 'One verified verse is selected and delivered each day at the time you choose.'
            },
            {
                label: 'Optional short reflection included',
                title: 'Example: a one-minute reflection',
                example: 'Two or three sentences connect the verse to ordinary life, followed by one simple question to carry into the day.',
                fulfillment: 'Turn reflections on or off without changing your daily verse delivery.'
            },
            {
                label: 'Delivery by email',
                title: 'Example: a calm morning email',
                example: 'A clean email with the verse, optional reflection, and one button to open the full passage in Prayer Guide.',
                fulfillment: 'During beta, delivery is restricted to the verified Google address on your signed-in account.'
            },
            {
                label: 'Delivery by SMS/text',
                title: 'Example: a concise text',
                example: '“Today’s verse · Micah 6:8” followed by the verse text and a short link to the optional reflection.',
                fulfillment: 'A single daily text, with clear opt-out controls and no unrelated promotional messages.'
            },
            {
                label: 'Slack delivery option',
                title: 'Example: a private Slack message',
                example: 'The daily verse arrives as a direct message in Slack with the reflection tucked below it.',
                fulfillment: 'You connect a Slack workspace and choose the private destination and delivery time.'
            },
            {
                label: 'Google Chat delivery option',
                title: 'Example: a Google Chat message',
                example: 'The daily verse arrives in your selected Google Chat space or direct conversation, ready when you open work.',
                fulfillment: 'You choose the Chat destination and can disconnect delivery whenever you want.'
            }
        ]
    },
    {
        id: 'audio',
        icon: Headphones,
        eyebrow: 'Listen',
        title: 'Audio scripture',
        description: 'A listenable scripture practice for moments when reading is difficult or inconvenient.',
        cta: 'I’m interested',
        features: [
            {
                label: 'Daily verse converted to audio',
                title: 'Example: today’s verse recording',
                example: 'A short audio track begins with the passage reference, reads the verse clearly, and leaves a few quiet seconds at the end.',
                fulfillment: 'A fresh, playable audio version accompanies the selected verse each day.'
            },
            {
                label: 'Warm natural voice narration',
                title: 'Example: calm, unhurried narration',
                example: 'The verse is spoken at a reflective pace with clear pronunciation and no distracting music beneath it.',
                fulfillment: 'Every recording is checked for clarity, pacing, and a tone appropriate for prayer.'
            },
            {
                label: 'Short devotional audio option',
                title: 'Example: a three-minute devotional',
                example: 'Listen to the verse, a brief reflection, one question to consider, and a closing prayer in a single track.',
                fulfillment: 'Choose verse-only audio or the longer devotional version before delivery.'
            },
            {
                label: 'Playable from phone or inbox',
                title: 'Example: one-tap listening',
                example: 'Tap play directly from your phone-friendly daily page or follow the audio link from your email.',
                fulfillment: 'No special audio app is required; the track opens in a simple web player.'
            },
            {
                label: 'Designed for morning or evening listening',
                title: 'Example: choose the pace of your day',
                example: 'A morning version gently prepares you for the day; an evening version creates space to reflect and release it.',
                fulfillment: 'Select your preferred listening time and change it as your routine changes.'
            },
            {
                label: 'No reading required',
                title: 'Example: listen with your eyes closed',
                example: 'The complete verse, reference, reflection, and prayer are spoken aloud so the experience stands on its own.',
                fulfillment: 'All essential content in the selected experience is included in the audio.'
            }
        ]
    },
    {
        id: 'gift',
        icon: Gift,
        eyebrow: 'Gifting roadmap',
        title: 'Consent-first gifting',
        description: 'A planned way to invite someone into encouragement without enrolling an address they have not verified and approved.',
        cta: 'Tell me when it’s ready',
        features: [
            {
                label: 'Invitation before delivery',
                title: 'Planned: invite a friend to choose',
                example: 'Share an invitation explaining the proposed verse-and-prayer rhythm without enrolling the recipient automatically.',
                fulfillment: 'The beta cannot send to someone else. A future flow would keep delivery off until the recipient accepts.'
            },
            {
                label: 'Recipient double opt-in',
                title: 'Planned: verified acceptance',
                example: 'The recipient signs in with their own Google account, reviews the invitation, and explicitly confirms participation.',
                fulfillment: 'Possessing an email address is never treated as consent. The recipient must verify and approve their own destination.'
            },
            {
                label: 'Optional personal note',
                title: 'Planned: explain why you shared it',
                example: 'Add a short note to the invitation so the recipient knows who it came from and why you thought of them.',
                fulfillment: 'A note would accompany the invitation only; it would not bypass recipient acceptance or start recurring delivery.'
            },
            {
                label: 'Recipient chooses the rhythm',
                title: 'Planned: confirm the schedule',
                example: 'After accepting, the recipient chooses whether and when encouragement should arrive.',
                fulfillment: 'The recipient—not the sender—would confirm the final schedule and could pause or stop it at any time.'
            },
            {
                label: 'Verified recipient destination',
                title: 'Planned: delivery to their own account',
                example: 'Email would go only to the Google address the recipient verified while accepting the invitation.',
                fulfillment: 'Arbitrary friend, family, or gift-recipient addresses remain unsupported until this consent flow exists.'
            },
            {
                label: 'Clear stop controls',
                title: 'Planned: recipient-controlled delivery',
                example: 'Every recurring message includes a clear way for the recipient to pause or stop future delivery.',
                fulfillment: 'The recipient would not need the sender to change or end an accepted schedule.'
            }
        ]
    }
];

const Premium = () => {
    const { configured, user } = useAuth();
    const [requested, setRequested] = useState({});
    const [activeOfferingIndex, setActiveOfferingIndex] = useState(0);
    const [activeFeatureIndex, setActiveFeatureIndex] = useState(0);
    const [preference, setPreference] = useState(getPremiumPreference);
    const [customRequest, setCustomRequest] = useState('');

    const request = (offering) => {
        const eventType = offering.id === 'gift' ? 'gift_intent' : 'subscribe_intent';
        emitCrmEvent(eventType, { offering: offering.id });
        setRequested((prev) => ({ ...prev, [offering.id]: true }));
    };

    const showOffering = (offeringIndex) => {
        setActiveOfferingIndex(offeringIndex);
        setActiveFeatureIndex(0);
    };

    const moveOffering = (direction) => {
        showOffering((activeOfferingIndex + direction + offerings.length) % offerings.length);
    };

    const chooseFree = () => {
        setPremiumPreference('free');
        setPreference('free');
    };

    const exploreExtras = () => {
        setPremiumPreference('explore');
        setPreference('explore');
    };

    const needsSignIn = configured && !user;
    const activeOffering = offerings[activeOfferingIndex];
    const activeFeature = activeOffering.features[activeFeatureIndex];
    const done = requested[activeOffering.id];
    const exampleId = `${activeOffering.id}-example`;
    const supportEmail = process.env.REACT_APP_SUPPORT_EMAIL || '';
    const hasSupportEmail = Boolean(supportEmail);
    const inquiryHref = `mailto:${supportEmail}?subject=${encodeURIComponent('Prayer Guide access or delivery idea')}&body=${encodeURIComponent(
        `I would like to ask about a custom Prayer Guide option:\n\n${customRequest}\n\nWho it would help:\n\nThe best way to reach me:\n`
    )}`;

    return (
        <ScreenLayout title="Premium" icon={Sparkles} tone="sand" eyebrow="Support the mission">
            <section className="premium-hero">
                <p className="shared-kicker"><Sparkles size={14} /> Freemium, honestly</p>
                <h1>Help keep this going.</h1>
                <p>
                    The whole prayer app stays free. These extras cover the costs of running it
                    and add thoughtful, practical support. Explore each offering so you know exactly
                    what we are considering and what you would receive.
                </p>
            </section>

            <section className="premium-human-promise" aria-labelledby="premium-human-promise-title">
                <span className="premium-human-promise-icon"><HeartHandshake size={23} /></span>
                <div>
                    <p className="shared-kicker"><MessageCircle size={13} /> Our personal promise</p>
                    <h2 id="premium-human-promise-title">Personal means a person is involved.</h2>
                    <p>
                        Personalized plans begin with a real conversation or written intake and receive
                        human review before delivery. Technology may help with formatting and delivery;
                        it does not replace the care, judgment, or final word of a person.
                    </p>
                </div>
            </section>

            <section className="premium-free-choice" aria-labelledby="premium-free-title">
                <div className="premium-free-copy">
                    <p className="shared-kicker"><BadgeCheck size={13} /> No pressure, ever</p>
                    <h2 id="premium-free-title">The free app is the real app.</h2>
                    <p>
                        Prayer, scripture, journaling, and the core experience stay free. Optional extras
                        support the work; they do not unlock your ability to pray.
                    </p>
                </div>
                {preference === 'free' ? (
                    <div className="premium-free-confirmation" role="status">
                        <Check size={19} />
                        <span>
                            <strong>Free selected.</strong>
                            You will not see premium pitches elsewhere in the app.
                        </span>
                        <button type="button" onClick={exploreExtras}>
                            <RefreshCcw size={14} /> Browse optional extras
                        </button>
                    </div>
                ) : (
                    <button type="button" className="premium-free-button" onClick={chooseFree}>
                        <Check size={17} />
                        Keep my experience free and hide sales pitches
                    </button>
                )}
            </section>

            {needsSignIn && (
                <div className="premium-signin-note">
                    <span>Sign in so we can save your interest and reach you when it’s ready.</span>
                    <Link to="/login" className="premium-signin-button">Sign in</Link>
                </div>
            )}

            {preference !== 'free' && (
                <section className="premium-carousel" aria-labelledby="premium-carousel-title">
                    <div className="premium-carousel-intro">
                        <div>
                            <p className="shared-kicker"><Sparkles size={13} /> Optional ways to go further</p>
                            <h2 id="premium-carousel-title">Explore one idea at a time.</h2>
                        </div>
                        <span>{activeOfferingIndex + 1} of {offerings.length}</span>
                    </div>

                    <nav className="premium-progress" aria-label="Premium offering progress">
                        {offerings.map((offering, offeringIndex) => (
                            <button
                                type="button"
                                key={offering.id}
                                className={offeringIndex === activeOfferingIndex ? 'is-active' : ''}
                                aria-current={offeringIndex === activeOfferingIndex ? 'step' : undefined}
                                onClick={() => showOffering(offeringIndex)}
                            >
                                <span>{offeringIndex + 1}</span>
                                <strong>{offering.title}</strong>
                            </button>
                        ))}
                    </nav>

                    <article className="premium-card" key={activeOffering.id}>
                        <div className="premium-card-content">
                            <div className="premium-card-details">
                                <div className="premium-card-heading">
                                    <span className="premium-card-icon"><activeOffering.icon size={22} /></span>
                                    <div>
                                        <small>{activeOffering.eyebrow}</small>
                                        <h2>{activeOffering.title}</h2>
                                    </div>
                                </div>
                                <p className="premium-card-summary">{activeOffering.description}</p>

                                <p className="premium-explore-note">Hover, focus, or tap an item to see what it includes.</p>
                                <ul className="premium-feature-list">
                                    {activeOffering.features.map((feature, featureIndex) => {
                                        const isActive = featureIndex === activeFeatureIndex;
                                        return (
                                            <li key={feature.label}>
                                                <button
                                                    type="button"
                                                    className={`premium-feature-link ${isActive ? 'is-active' : ''}`}
                                                    aria-controls={exampleId}
                                                    aria-pressed={isActive}
                                                    onClick={() => setActiveFeatureIndex(featureIndex)}
                                                    onFocus={() => setActiveFeatureIndex(featureIndex)}
                                                    onMouseEnter={() => setActiveFeatureIndex(featureIndex)}
                                                >
                                                    <span>{feature.label}</span>
                                                    <ChevronRight size={15} aria-hidden="true" />
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>

                            <div className="premium-card-preview">
                                <div className="premium-example" id={exampleId} role="region" aria-live="polite">
                                    <small>Example artifact</small>
                                    <h3>{activeFeature.title}</h3>
                                    <p>{activeFeature.example}</p>
                                    <div className="premium-fulfillment">
                                        <HeartHandshake size={17} aria-hidden="true" />
                                        <span><strong>What we provide:</strong> {activeFeature.fulfillment}</span>
                                    </div>
                                </div>

                                <button
                                    className={`premium-card-button ${done ? 'is-done' : ''}`}
                                    onClick={() => request(activeOffering)}
                                    disabled={done || needsSignIn}
                                >
                                    {done ? <><Check size={16} /> You’re on the list</> : activeOffering.cta}
                                </button>
                            </div>
                        </div>
                    </article>

                    <div className="premium-carousel-controls">
                        <button type="button" onClick={() => moveOffering(-1)} aria-label="Show previous offering">
                            <ArrowLeft size={17} /> Previous
                        </button>
                        <p>Optional extras only. The heart of Prayer Guide stays free.</p>
                        <button type="button" onClick={() => moveOffering(1)} aria-label="Show next offering">
                            Next <ArrowRight size={17} />
                        </button>
                    </div>
                </section>
            )}

            <section className="premium-custom" aria-labelledby="premium-custom-title">
                <div className="premium-custom-copy">
                    <p className="shared-kicker"><HeartHandshake size={13} /> Make access possible</p>
                    <h2 id="premium-custom-title">Have something else in mind? Ask us.</h2>
                    <p>
                        We are willing to explore thoughtful, unusual requests. That might mean sending
                        scripture to someone in jail, supporting a care facility, reaching a person without
                        reliable internet, or creating something for a need we have not considered yet.
                        The beta cannot send to another person's address, and any future third-party
                        delivery will require that recipient's explicit consent.
                    </p>
                    <div className="premium-custom-examples" aria-label="Examples of custom requests">
                        <span><Building2 size={16} /> Jails and care settings</span>
                        <span><Accessibility size={16} /> Access needs</span>
                        <span><Users size={16} /> Groups and communities</span>
                    </div>
                </div>
                <div className="premium-custom-form">
                    <label htmlFor="premium-custom-request">What would help?</label>
                    <textarea
                        id="premium-custom-request"
                        value={customRequest}
                        onChange={(event) => setCustomRequest(event.target.value)}
                        placeholder="Tell us who you hope to reach and what would make it possible..."
                        rows="5"
                    />
                    <a className="premium-custom-button" href={inquiryHref}>
                        <MailPlus size={17} /> Prepare request email
                    </a>
                    <small>
                        {hasSupportEmail
                            ? 'Your email app will open so you can review the request before sending it.'
                            : 'Your email app will open. Add the right recipient before sending until a support address is configured.'}
                    </small>
                </div>
            </section>

            <p className="premium-fineprint">
                Nothing is charged today. Prayer Guide remains useful without selecting any optional extra.
            </p>
        </ScreenLayout>
    );
};

export default Premium;
